import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'

const RFQ_STATUS = {
  draft:     { label: 'Draft',     bg: 'bg-gray-100',    text: 'text-gray-600' },
  issued:    { label: 'Issued',    bg: 'bg-blue-100',    text: 'text-blue-700' },
  closed:    { label: 'Closed',    bg: 'bg-amber-100',   text: 'text-amber-700' },
  evaluated: { label: 'Evaluated', bg: 'bg-green-100',   text: 'text-green-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100',    text: 'text-gray-400' },
}

function ConfirmOverlay({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
      <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
        <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg">Delete</button>
        </div>
      </div>
    </div>
  )
}

function fmtN(n) {
  if (!n && n !== 0) return '—'
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white
      ${type === 'error' ? 'bg-red-600' : 'bg-[#1A7A4A]'}`}>
      {message}
    </div>
  )
}

function StatusBadge({ status, config }) {
  const c = config[status] || config[Object.keys(config)[0]]
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>
}

const EMPTY_QUOTE_LINE = { description: '', unit: '', quantity: '', unit_rate: '', sub_code: '', ref_code: '', cost_code: '', cost_code_desc: '' }
const EMPTY_EQ_QUOTE_LINE = { equipment_type: '', description: '', unit: 'Hours', quantity: '', unit_rate: '', sub_code: '', cost_code: '', cost_code_desc: '' }

export default function RFQQuotations({ initProject }) {
  const [projects, setProjects]           = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  // Real authorization source -- can() mirrors has_permission() exactly,
  // same hook pattern as IPC/PO/PR/Subcontract/Inventory.
  const { can } = useProjectAuth(selectedProject?.id)
  const [rfqs, setRfqs]                   = useState([])
  const [prs, setPrs]                     = useState([])
  const [loading, setLoading]             = useState(false)
  const [toast, setToast]                 = useState(null)
  const [activeTab, setActiveTab]         = useState('rfq') // rfq | comparison
  const [mainTab, setMainTab]             = useState('material') // material | equipment
  const [showRFQModal, setShowRFQModal]   = useState(false)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const quoteFileRef = useRef(null)
  const [editRFQ, setEditRFQ]             = useState(null)
  const [selectedRFQ, setSelectedRFQ]     = useState(null)
  const [quotations, setQuotations]       = useState([])
  const [rfqForm, setRfqForm]             = useState({ title: '', scope_description: '', pr_id: '', closing_date: '' })
  const [quoteForm, setQuoteForm]         = useState({ supplier_name: '', vendor_id: '', supplier_contact: '', quotation_ref: '', submission_date: '', validity_days: 30, delivery_days: '', payment_terms: '', notes: '' })
  const [vendorSearch, setVendorSearch]   = useState('')
  const [vendorResults, setVendorResults] = useState([])
  const [showVendorDD, setShowVendorDD]   = useState(false)
  const vendorSearchTimer = useRef(null)
  const [quoteLines, setQuoteLines]       = useState([{ ...EMPTY_QUOTE_LINE }])
  const [subCodes, setSubCodes]           = useState([]) // cost_codes level 2
  const [materials, setMaterials]         = useState([]) // material master
  const [costCodes, setCostCodes]         = useState([]) // cost_codes level 1

  // ── Equipment RFQ (separate tables) ──
  const [eqActiveTab, setEqActiveTab]     = useState('rfq') // rfq | comparison
  const [eqRfqs, setEqRfqs]               = useState([])
  const [eqPrs, setEqPrs]                 = useState([])
  const [eqShowRFQModal, setEqShowRFQModal] = useState(false)
  const [eqShowQuoteModal, setEqShowQuoteModal] = useState(false)
  const eqQuoteFileRef = useRef(null)
  const [eqEditRFQ, setEqEditRFQ]         = useState(null)
  const [eqSelectedRFQ, setEqSelectedRFQ] = useState(null)
  const [eqQuotations, setEqQuotations]   = useState([])
  const [eqRfqForm, setEqRfqForm]         = useState({ title: '', scope_description: '', pr_id: '', closing_date: '' })
  const [eqQuoteForm, setEqQuoteForm]     = useState({ supplier_name: '', vendor_id: '', supplier_contact: '', quotation_ref: '', submission_date: '', validity_days: 30, delivery_days: '', payment_terms: '', notes: '' })
  const [eqVendorSearch, setEqVendorSearch] = useState('')
  const [eqVendorResults, setEqVendorResults] = useState([])
  const [eqShowVendorDD, setEqShowVendorDD] = useState(false)
  const eqVendorSearchTimer = useRef(null)
  const [eqQuoteLines, setEqQuoteLines]   = useState([{ ...EMPTY_EQ_QUOTE_LINE }])
  const [eqCostCodes, setEqCostCodes]     = useState([]) // cost_codes level 1 (equipment)
  const [eqConfirmDelete, setEqConfirmDelete] = useState(null)

  const showToast = (message, type = 'success') => setToast({ message, type })

  useEffect(() => {
    supabase.from('projects').select('id, name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProject) setSelectedProject(initProject)
  }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadRFQs(selectedProject.id)
      supabase.from('purchase_requisitions').select('id, pr_number, description').eq('project_id', selectedProject.id).eq('status', 'approved').order('pr_number').then(({ data, error }) => {
        if (error) console.error('PRs fetch error:', error.message)
        setPrs(data || [])
      })
      // Load sub_codes from cost_codes level 2, with resolved Cost Code + Resource Code ancestry
      supabase.from('cost_codes').select('id, code, description, level, parent_id').eq('project_id', selectedProject.id).order('code')
        .then(({ data }) => {
          const all = data || []
          const byId = {}
          all.forEach(c => { byId[c.id] = c })
          const subs = all.filter(c => parseInt(c.level) === 2).map(sub => {
            const parent = byId[sub.parent_id]
            let cc = null, rc = null
            if (parent && parseInt(parent.level) === 4) {
              rc = parent
              cc = byId[parent.parent_id] || null
            } else if (parent && parseInt(parent.level) === 1) {
              cc = parent
            }
            return {
              ...sub,
              costCodeId: cc?.id || null,
              costCode: cc?.code || null,
              costCodeDescription: cc?.description || null,
              resourceCode: rc?.code || null,
              resourceCodeDescription: rc?.description || null,
            }
          })
          setSubCodes(subs)
        })
      // Load cost_codes level 1
      supabase.from('cost_codes').select('id, code, description').eq('project_id', selectedProject.id).eq('level', 1).order('code')
        .then(({ data }) => setCostCodes(data || []))
      // Load materials
      supabase.from('materials').select('id, material_code, description, unit').eq('project_id', selectedProject.id).order('material_code')
        .then(({ data }) => setMaterials(data || []))
      // Equipment: load RFQs, approved equipment PRs, cost_codes
      loadEqRFQs(selectedProject.id)
      supabase.from('equipment_prs').select('id, pr_number, description').eq('project_id', selectedProject.id).eq('status', 'approved').order('pr_number').then(({ data, error }) => {
        if (error) console.error('Equipment PRs fetch error:', error.message)
        setEqPrs(data || [])
      })
      supabase.from('cost_codes').select('id, code, description').eq('project_id', selectedProject.id).eq('level', 1).order('code')
        .then(({ data }) => setEqCostCodes(data || []))
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadRFQs = useCallback(async (projectId) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('rfq_requests')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
    if (error) console.error('loadRFQs error:', error.message)
    setRfqs(data || [])
    setLoading(false)
  }, [])

  const loadQuotations = useCallback(async (rfqId) => {
    const { data } = await supabase
      .from('quotations')
      .select('*, quotation_line_items(*)')
      .eq('rfq_id', rfqId)
      .order('created_at')
    setQuotations(data || [])
  }, [])

  // ═══ Equipment RFQ ═══
  const loadEqRFQs = useCallback(async (projectId) => {
    const { data, error } = await supabase
      .from('equipment_rfqs')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
    if (error) console.error('loadEqRFQs error:', error.message)
    setEqRfqs(data || [])
  }, [])

  const loadEqQuotations = useCallback(async (rfqId) => {
    const { data } = await supabase
      .from('equipment_quotations')
      .select('*, equipment_quotation_line_items(*)')
      .eq('rfq_id', rfqId)
      .order('created_at')
    setEqQuotations(data || [])
  }, [])

  const openNewRFQ = () => {
    setEditRFQ(null)
    setRfqForm({ title: '', scope_description: '', pr_id: '', closing_date: '' })
    // Reload approved PRs fresh
    if (selectedProject?.id) {
      supabase.from('purchase_requisitions').select('id, pr_number, description').eq('project_id', selectedProject.id).eq('status', 'approved').order('pr_number').then(({ data, error }) => {
        if (error) console.error('PRs fetch error:', error.message)
        setPrs(data || [])
      })
    }
    setShowRFQModal(true)
  }

  const handleSaveRFQ = async () => {
    if (!rfqForm.title.trim()) return showToast('العنوان مطلوب', 'error')
    const { data: cnt } = await supabase.from('rfq_requests').select('id').eq('project_id', selectedProject.id)
    const rfqNumber = `RFQ-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`
    const payload = { ...rfqForm, project_id: selectedProject.id, rfq_number: rfqNumber, pr_id: rfqForm.pr_id || null }
    const { error } = editRFQ
      ? await supabase.from('rfq_requests').update(payload).eq('id', editRFQ.id)
      : await supabase.from('rfq_requests').insert(payload)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(editRFQ ? 'تم التحديث' : 'تم الإنشاء')
    setShowRFQModal(false)
    loadRFQs(selectedProject.id)
  }

  const handleStatusRFQ = async (rfq, status) => {
    await supabase.from('rfq_requests').update({ status }).eq('id', rfq.id)
    showToast('تم التحديث')
    loadRFQs(selectedProject.id)
  }

  // ═══ Equipment RFQ ═══
  const openNewEqRFQ = () => {
    setEqEditRFQ(null)
    setEqRfqForm({ title: '', scope_description: '', pr_id: '', closing_date: '' })
    if (selectedProject?.id) {
      supabase.from('equipment_prs').select('id, pr_number, description').eq('project_id', selectedProject.id).eq('status', 'approved').order('pr_number').then(({ data, error }) => {
        if (error) console.error('Equipment PRs fetch error:', error.message)
        setEqPrs(data || [])
      })
    }
    setEqShowRFQModal(true)
  }

  const handleSaveEqRFQ = async () => {
    if (!eqRfqForm.title.trim()) return showToast('العنوان مطلوب', 'error')
    const { data: cnt } = await supabase.from('equipment_rfqs').select('id').eq('project_id', selectedProject.id)
    const rfqNumber = `ERFQ-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`
    const payload = { ...eqRfqForm, project_id: selectedProject.id, rfq_number: rfqNumber, pr_id: eqRfqForm.pr_id || null }
    const { error } = eqEditRFQ
      ? await supabase.from('equipment_rfqs').update(payload).eq('id', eqEditRFQ.id)
      : await supabase.from('equipment_rfqs').insert(payload)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(eqEditRFQ ? 'تم التحديث' : 'تم الإنشاء')
    setEqShowRFQModal(false)
    loadEqRFQs(selectedProject.id)
  }

  const handleStatusEqRFQ = async (rfq, status) => {
    await supabase.from('equipment_rfqs').update({ status }).eq('id', rfq.id)
    showToast('تم التحديث')
    loadEqRFQs(selectedProject.id)
  }

  const openAddQuote = (rfq) => {
    setSelectedRFQ(rfq)
    setQuoteForm({ supplier_name: '', vendor_id: '', supplier_contact: '', quotation_ref: '', submission_date: '', validity_days: 30, delivery_days: '', payment_terms: '', notes: '' })
    setVendorSearch('')
    setVendorResults([])
    setQuoteLines([{ ...EMPTY_QUOTE_LINE }])
    setShowQuoteModal(true)
  }

  // Vendor typeahead search (Vendor Master) — debounced
  function searchVendors(q) {
    if (vendorSearchTimer.current) clearTimeout(vendorSearchTimer.current)
    if (!q || q.length < 2) { setVendorResults([]); return }
    vendorSearchTimer.current = setTimeout(async () => {
      const { data } = await supabase.from('vendors').select('id,trade_name,legal_name,vat_number,payment_terms,contact_name,contact_phone')
        .or(`trade_name.ilike.%${q}%,legal_name.ilike.%${q}%`).eq('is_approved', true).limit(8)
      setVendorResults(data || [])
    }, 300)
  }

  function selectVendor(v) {
    setQuoteForm(p => ({ ...p, supplier_name: v.trade_name, vendor_id: v.id, supplier_contact: v.contact_name || p.supplier_contact, payment_terms: v.payment_terms || p.payment_terms }))
    setVendorSearch(v.trade_name)
    setShowVendorDD(false)
    setVendorResults([])
  }

  // ═══ Equipment RFQ ═══
  const openAddEqQuote = (rfq) => {
    setEqSelectedRFQ(rfq)
    setEqQuoteForm({ supplier_name: '', vendor_id: '', supplier_contact: '', quotation_ref: '', submission_date: '', validity_days: 30, delivery_days: '', payment_terms: '', notes: '' })
    setEqVendorSearch('')
    setEqVendorResults([])
    setEqQuoteLines([{ ...EMPTY_EQ_QUOTE_LINE }])
    setEqShowQuoteModal(true)
  }

  function searchEqVendors(q) {
    if (eqVendorSearchTimer.current) clearTimeout(eqVendorSearchTimer.current)
    if (!q || q.length < 2) { setEqVendorResults([]); return }
    eqVendorSearchTimer.current = setTimeout(async () => {
      const { data } = await supabase.from('vendors').select('id,trade_name,legal_name,vat_number,payment_terms,contact_name,contact_phone')
        .or(`trade_name.ilike.%${q}%,legal_name.ilike.%${q}%`).eq('is_approved', true).limit(8)
      setEqVendorResults(data || [])
    }, 300)
  }

  function selectEqVendor(v) {
    setEqQuoteForm(p => ({ ...p, supplier_name: v.trade_name, vendor_id: v.id, supplier_contact: v.contact_name || p.supplier_contact, payment_terms: v.payment_terms || p.payment_terms }))
    setEqVendorSearch(v.trade_name)
    setEqShowVendorDD(false)
    setEqVendorResults([])
  }

  const handleSaveEqQuote = async () => {
    if (!eqQuoteForm.supplier_name.trim()) return showToast('اسم المورد مطلوب', 'error')
    if (!eqQuoteForm.vendor_id) return showToast('اختار المورد من Vendor Master (اكتب حرفين على الأقل واختار من القايمة)', 'error')
    const linesTotal = eqQuoteLines.reduce((s, l) => s + ((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_rate) || 0)), 0)
    const payload = {
      rfq_id:          eqSelectedRFQ.id,
      project_id:      selectedProject.id,
      supplier_name:   eqQuoteForm.supplier_name,
      vendor_id:       eqQuoteForm.vendor_id,
      quotation_ref:   eqQuoteForm.quotation_ref || null,
      submission_date: eqQuoteForm.submission_date || null,
      delivery_days:   parseInt(eqQuoteForm.delivery_days) || null,
      payment_terms:   eqQuoteForm.payment_terms || null,
      notes:           eqQuoteForm.notes || null,
      total_amount:    linesTotal || null,
      validity_days:   parseInt(eqQuoteForm.validity_days) || 30,
      status:          'submitted',
    }
    const { data, error } = await supabase.from('equipment_quotations').insert(payload).select().single()
    if (error) return showToast('فشل: ' + error.message, 'error')
    const validLines = eqQuoteLines.filter(l => l.equipment_type?.trim())
    if (validLines.length > 0) {
      await supabase.from('equipment_quotation_line_items').insert(
        validLines.map((l, i) => ({
          quotation_id: data.id,
          equipment_type: l.equipment_type,
          description:  l.description || null,
          unit:         l.unit || 'Hours',
          quantity:     parseFloat(l.quantity) || null,
          unit_rate:    parseFloat(l.unit_rate) || null,
          sub_code:     l.sub_code || null,
          cost_code:    l.cost_code || null,
          sort_order:   i,
        }))
      )
    }
    showToast('تم إضافة الـ Quotation')
    setEqShowQuoteModal(false)
    loadEqRFQs(selectedProject.id)
    if (eqActiveTab === 'comparison' && eqSelectedRFQ) loadEqQuotations(eqSelectedRFQ.id)
  }

  const handleSaveQuote = async () => {
    if (!quoteForm.supplier_name.trim()) return showToast('اسم المورد مطلوب', 'error')
    if (!quoteForm.vendor_id) return showToast('اختار المورد من Vendor Master (اكتب حرفين على الأقل واختار من القايمة)', 'error')
    const linesTotal = quoteLines.reduce((s, l) => s + ((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_rate) || 0)), 0)
    const payload = {
      rfq_id:          selectedRFQ.id,
      project_id:      selectedProject.id,
      supplier_name:   quoteForm.supplier_name,
      vendor_id:       quoteForm.vendor_id,
      quotation_ref:   quoteForm.quotation_ref || null,
      submission_date: quoteForm.submission_date || null,
      delivery_days:   parseInt(quoteForm.delivery_days) || null,
      payment_terms:   quoteForm.payment_terms || null,
      notes:           quoteForm.notes || null,
      total_amount:    linesTotal || null,
      validity_days:   parseInt(quoteForm.validity_days) || 30,
      status:          'submitted',
    }
    const { data, error } = await supabase.from('quotations').insert(payload).select().single()
    if (error) return showToast('فشل: ' + error.message, 'error')
    const validLines = quoteLines.filter(l => l.description?.trim())
    if (validLines.length > 0) {
      await supabase.from('quotation_line_items').insert(
        validLines.map((l, i) => ({
          quotation_id: data.id,
          description:  l.description,
          unit:         l.unit || null,
          quantity:     parseFloat(l.quantity) || null,
          unit_rate:    parseFloat(l.unit_rate) || null,
          sub_code:     l.sub_code || null,
          ref_code:     l.ref_code || null,
          cost_code:    l.cost_code || null,
          sort_order:   i,
        }))
      )
    }
    showToast('تم إضافة الـ Quotation')
    setShowQuoteModal(false)
    loadRFQs(selectedProject.id)
    if (activeTab === 'comparison' && selectedRFQ) loadQuotations(selectedRFQ.id)
  }

  const handleQuoteExcelUpload = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' })
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
      const parsed = rows.map(r => ({
        description: String(r.description || r['Description'] || r['DESCRIPTION'] || '').trim(),
        sub_code:    String(r.sub_code    || r['Sub Code']   || r['SUB CODE']   || '').trim(),
        ref_code:    String(r.ref_code    || r['Ref Code']   || r['REF CODE']   || '').trim(),
        unit:        String(r.unit        || r['Unit']       || r['UNIT']       || '').trim(),
        quantity:    String(r.quantity    || r['Qty']        || r['QTY']        || '').trim(),
        unit_rate:   String(r.unit_rate   || r['Rate']       || r['RATE']       || '').trim(),
        cost_code:   '',
        cost_code_desc: '',
      })).filter(r => r.description)
      // Auto-fill cost_code + resource_code from sub_code
      const enriched = parsed.map(r => {
        const sc = subCodes.find(s => s.code === r.sub_code)
        return { ...r, cost_code: sc?.costCode || '', cost_code_desc: sc?.costCodeDescription || '', resource_code: sc?.resourceCode || '' }
      })
      setQuoteLines(enriched.length ? enriched : [{ ...EMPTY_QUOTE_LINE }])
      showToast(`تم تحميل ${enriched.length} بند`)
    }
    reader.readAsArrayBuffer(file)
    if (quoteFileRef.current) quoteFileRef.current.value = ''
  }

  const downloadQuoteTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['description', 'sub_code', 'ref_code', 'unit', 'quantity', 'unit_rate'],
      ['Electrical Submersible Pump 300WQ800', 'Mat-Dew2', 'PUMP-300WQ800', 'SET', 4, 300000],
      ['Impeller for Submersible Pump', 'Mat-Dew2', 'IMP-300WQ800', 'EACH', 4, 15000],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Quotation Lines')
    XLSX.writeFile(wb, 'quotation_lines_template.xlsx')
  }

  // ═══ Equipment RFQ ═══
  const handleEqQuoteExcelUpload = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' })
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
      const parsed = rows.map(r => ({
        equipment_type: String(r.equipment_type || r['Equipment Type'] || r['subcode'] || '').trim(),
        description: String(r.description || r['Description'] || '').trim(),
        sub_code:    String(r.sub_code    || r['Sub Code']   || '').trim(),
        unit:        String(r.unit        || r['Unit']       || 'Hours').trim(),
        quantity:    String(r.quantity    || r['Qty']        || '').trim(),
        unit_rate:   String(r.unit_rate   || r['Rate']       || '').trim(),
        cost_code:   '',
        cost_code_desc: '',
      })).filter(r => r.equipment_type)
      setEqQuoteLines(parsed.length ? parsed : [{ ...EMPTY_EQ_QUOTE_LINE }])
      showToast(`تم تحميل ${parsed.length} بند`)
    }
    reader.readAsArrayBuffer(file)
    if (eqQuoteFileRef.current) eqQuoteFileRef.current.value = ''
  }

  const downloadEqQuoteTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['equipment_type', 'description', 'sub_code', 'unit', 'quantity', 'unit_rate'],
      ['Excavator', 'Excavator 20 ton', '', 'Hours', 200, 65],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment Quotation Lines')
    XLSX.writeFile(wb, 'equipment_quotation_lines_template.xlsx')
  }

  const handleUnrecommend = async (rfqId) => {
    await supabase.from('quotations').update({ is_recommended: false, status: 'submitted' }).eq('rfq_id', rfqId)
    loadQuotations(rfqId)
  }

  const handleRecommend = async (rfqId, quoteId) => {
    await supabase.from('quotations').update({ is_recommended: false }).eq('rfq_id', rfqId)
    await supabase.from('quotations').update({ is_recommended: true, status: 'selected' }).eq('id', quoteId)
    showToast('تم التحديد كـ Recommended')
    loadRFQs(selectedProject.id)
    loadQuotations(rfqId)
  }

  // ═══ Equipment RFQ ═══
  const handleUnrecommendEq = async (rfqId) => {
    await supabase.from('equipment_quotations').update({ is_recommended: false, status: 'submitted' }).eq('rfq_id', rfqId)
    loadEqQuotations(rfqId)
  }

  const handleRecommendEq = async (rfqId, quoteId) => {
    await supabase.from('equipment_quotations').update({ is_recommended: false }).eq('rfq_id', rfqId)
    await supabase.from('equipment_quotations').update({ is_recommended: true, status: 'selected' }).eq('id', quoteId)
    showToast('تم التحديد كـ Recommended')
    loadEqRFQs(selectedProject.id)
    loadEqQuotations(rfqId)
  }

  const handleAwardAndCreatePO = async (rfq, quote) => {
    if (!selectedProject?.id) return
    await supabase.from('quotations').update({ is_recommended: false }).eq('rfq_id', rfq.id)
    await supabase.from('quotations').update({ is_recommended: true, status: 'awarded' }).eq('id', quote.id)

    const { data: cnt } = await supabase.from('purchase_orders').select('id').eq('project_id', selectedProject.id)
    const poNumber = `PO-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`

    // Fetch cost_codes for lookup
    const { data: ccRows } = await supabase.from('cost_codes').select('id, code').eq('project_id', selectedProject.id)
    const ccMap = {}
    ;(ccRows || []).forEach(c => { ccMap[c.code] = c.id })

    // Lines from quotation
    const lines = quote.quotation_line_items || []
    const vatPct = 15
    const totalExcVat = lines.reduce((s, l) => s + ((parseFloat(l.quantity)||0) * (parseFloat(l.unit_rate)||0)), 0)
    const totalIncVat = totalExcVat * (1 + vatPct / 100)

    // Get first line data for PO header
    const firstLine = lines[0]
    const firstCCId = firstLine?.cost_code ? ccMap[firstLine.cost_code] || null : null
    const firstSubCode = firstLine?.sub_code || null

    const { data: po, error: poErr } = await supabase.from('purchase_orders').insert({
      project_id:    selectedProject.id,
      po_number:     poNumber,
      vendor:        quote.supplier_name,
      vendor_id:     quote.vendor_id || null,
      status:        'draft',
      approval_stage:'draft',
      po_type:       'standard',
      rfq_id:        rfq.id,
      quotation_id:  quote.id,
      pr_id:         rfq.pr_id || null,
      issue_date:    new Date().toISOString().split('T')[0],
      vat_pct:       vatPct,
      total_exc_vat: totalExcVat,
      total_inc_vat: totalIncVat,
      sub_code:      firstSubCode,
      cost_code_id:  firstCCId,
      cost_type:     'direct',
      notes:         `Created from RFQ ${rfq.rfq_number} — ${quote.quotation_ref || ''}`,
    }).select().single()
    if (poErr) return showToast('فشل إنشاء PO: ' + poErr.message, 'error')

    if (lines.length > 0) {
      const { error: linesErr } = await supabase.from('po_line_items').insert(
        lines.map(l => ({
          po_id:        po.id,
          description:  l.description,
          unit:         l.unit || null,
          qty:          parseFloat(l.quantity) || 0,
          unit_rate:    parseFloat(l.unit_rate) || 0,
          sub_code:     l.sub_code || null,
          ref_code:     l.ref_code || null,
          cost_code_id: l.cost_code ? ccMap[l.cost_code] || null : null,
          cost_code:    l.cost_code || null,
        }))
      )
      if (linesErr) return showToast('فشل حفظ الـ lines: ' + linesErr.message, 'error')
    }

    await supabase.from('rfq_requests').update({ status: 'evaluated' }).eq('id', rfq.id)
    await supabase.from('quotations').update({ po_id: po.id }).eq('id', quote.id)
    if (rfq.pr_id) {
      // Bugfix (Phase A4): status was 'converted', a value NOT permitted by
      // purchase_requisitions_status_check (allowed: draft/pending/approved/
      // rejected/po_issued/closed) -- this write silently failed every time
      // (no error was caught), leaving status stuck on 'approved'. Confirmed
      // by investigation this caused a real functional bug: RFQQuotations.jsx's
      // own PR picker (lines ~110/203) filters by status='approved', so a
      // fully-converted PR kept reappearing as "available for a new RFQ".
      // approval_stage: 'converted' is unaffected -- confirmed elsewhere in
      // the codebase (PurchaseRequisitions.jsx counters/filters) as the
      // actually-read column for conversion state; only the status value
      // needed correcting to the constraint's real "PO issued" term.
      const { error: prConvertErr } = await supabase
        .from('purchase_requisitions')
        .update({ status: 'po_issued', approval_stage: 'converted' })
        .eq('id', rfq.pr_id)
      if (prConvertErr) return showToast('فشل تحديث حالة الـ PR: ' + prConvertErr.message, 'error')
    }
    showToast(`✓ PO ${poNumber} created — ${quote.supplier_name}`)
    loadRFQs(selectedProject.id)
    loadQuotations(rfq.id)
  }

  // ═══ Equipment RFQ ═══
  const handleAwardAndCreateEqPO = async (rfq, quote) => {
    if (!selectedProject?.id) return
    await supabase.from('equipment_quotations').update({ is_recommended: false }).eq('rfq_id', rfq.id)
    await supabase.from('equipment_quotations').update({ is_recommended: true, status: 'awarded' }).eq('id', quote.id)

    const { data: cnt } = await supabase.from('rental_equipment_pos').select('id').eq('project_id', selectedProject.id)
    const poNumber = `EPO-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`

    const { data: ccRows } = await supabase.from('cost_codes').select('id, code').eq('project_id', selectedProject.id)
    const ccMap = {}
    ;(ccRows || []).forEach(c => { ccMap[c.code] = c.id })

    const lines = quote.equipment_quotation_line_items || []
    const vatPct = 15
    const totalExcVat = lines.reduce((s, l) => s + ((parseFloat(l.quantity)||0) * (parseFloat(l.unit_rate)||0)), 0)
    const totalIncVat = totalExcVat * (1 + vatPct / 100)

    const firstLine = lines[0]
    const firstCCId = firstLine?.cost_code ? ccMap[firstLine.cost_code] || null : null

    const { data: po, error: poErr } = await supabase.from('rental_equipment_pos').insert({
      project_id:    selectedProject.id,
      po_number:     poNumber,
      vendor:        quote.supplier_name,
      vendor_id:     quote.vendor_id || null,
      status:        'draft',
      approval_stage:'draft',
      po_type:       'equipment',
      cost_code_id:  firstCCId,
      issue_date:    new Date().toISOString().split('T')[0],
      vat_pct:       vatPct,
      total_exc_vat: totalExcVat,
      total_inc_vat: totalIncVat,
      notes:         `Created from RFQ ${rfq.rfq_number} — ${quote.quotation_ref || ''}`,
    }).select().single()
    if (poErr) return showToast('فشل إنشاء PO: ' + poErr.message, 'error')

    if (lines.length > 0) {
      const { error: linesErr } = await supabase.from('rental_equipment_po_line_items').insert(
        lines.map(l => ({
          po_id:        po.id,
          equipment_type: l.equipment_type,
          description:  l.description || null,
          quantity:     parseFloat(l.quantity) || 0,
          unit:         l.unit || 'Hours',
          rate:         parseFloat(l.unit_rate) || 0,
          total_cost:   (parseFloat(l.quantity)||0) * (parseFloat(l.unit_rate)||0),
          cost_code_id: l.cost_code ? ccMap[l.cost_code] || null : null,
        }))
      )
      if (linesErr) return showToast('فشل حفظ الـ lines: ' + linesErr.message, 'error')
    }

    await supabase.from('equipment_rfqs').update({ status: 'evaluated' }).eq('id', rfq.id)
    await supabase.from('equipment_quotations').update({ po_id: po.id }).eq('id', quote.id)
    if (rfq.pr_id) {
      await supabase.from('equipment_prs').update({ status: 'converted', approval_stage: 'converted' }).eq('id', rfq.pr_id)
    }
    showToast(`✓ Equipment PO ${poNumber} created — ${quote.supplier_name}`)
    loadEqRFQs(selectedProject.id)
    loadEqQuotations(rfq.id)
  }

  const [confirmDelete, setConfirmDelete] = useState(null)

  const handleDeleteRFQ = async (rfq) => {
    const { data: quotes } = await supabase.from('quotations').select('id').eq('rfq_id', rfq.id)
    if (quotes?.length) {
      const ids = quotes.map(q => q.id)
      await supabase.from('quotation_line_items').delete().in('quotation_id', ids)
      await supabase.from('quotations').delete().in('id', ids)
    }
    await supabase.from('rfq_requests').delete().eq('id', rfq.id)
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadRFQs(selectedProject.id)
  }

  // ═══ Equipment RFQ ═══
  const handleDeleteEqRFQ = async (rfq) => {
    const { data: quotes } = await supabase.from('equipment_quotations').select('id').eq('rfq_id', rfq.id)
    if (quotes?.length) {
      const ids = quotes.map(q => q.id)
      await supabase.from('equipment_quotation_line_items').delete().in('quotation_id', ids)
      await supabase.from('equipment_quotations').delete().in('id', ids)
    }
    await supabase.from('equipment_rfqs').delete().eq('id', rfq.id)
    showToast('تم الحذف')
    setEqConfirmDelete(null)
    loadEqRFQs(selectedProject.id)
  }

  const openComparison = (rfq) => {
    setSelectedRFQ(rfq)
    loadQuotations(rfq.id)
    setActiveTab('comparison')
  }

  // Comparison data
  const lowest    = quotations.length ? Math.min(...quotations.map(q => q.total_amount || Infinity)) : 0
  const average   = quotations.length ? quotations.reduce((s, q) => s + (q.total_amount || 0), 0) / quotations.filter(q => q.total_amount).length : 0
  const recommended = quotations.find(q => q.is_recommended)

  // ═══ Equipment RFQ ═══
  const openEqComparison = (rfq) => {
    setEqSelectedRFQ(rfq)
    loadEqQuotations(rfq.id)
    setEqActiveTab('comparison')
  }

  const eqLowest    = eqQuotations.length ? Math.min(...eqQuotations.map(q => q.total_amount || Infinity)) : 0
  const eqAverage   = eqQuotations.length ? eqQuotations.reduce((s, q) => s + (q.total_amount || 0), 0) / eqQuotations.filter(q => q.total_amount).length : 0
  const eqRecommended = eqQuotations.find(q => q.is_recommended)

  return (
    <div style={{minHeight:"100vh",background:"#F3F4F6",fontFamily:"inherit"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",padding:"18px 24px 14px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">RFQ & Quotation Comparison</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">طلبات العروض ومقارنة الأسعار</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-lg p-1">
              {[{id:'material',label:'📦 Material'},{id:'equipment',label:'🚜 Equipment'}].map(t => (
                <button key={t.id} onClick={() => setMainTab(t.id)}
                  className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${mainTab===t.id?'bg-orange-500 text-white':'text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-lg p-1">
              {mainTab === 'material' ? (
                [{id:'rfq',label:'📋 RFQs'},{id:'comparison',label:'📊 Comparison'}].map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)}
                    className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${activeTab===t.id?'bg-[#1C1A18] text-white':'text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                    {t.label}
                  </button>
                ))
              ) : (
                [{id:'rfq',label:'📋 RFQs'},{id:'comparison',label:'📊 Comparison'}].map(t => (
                  <button key={t.id} onClick={() => setEqActiveTab(t.id)}
                    className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${eqActiveTab===t.id?'bg-[#1C1A18] text-white':'text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                    {t.label}
                  </button>
                ))
              )}
            </div>
            {selectedProject && mainTab === 'material' && activeTab === 'rfq' && can('RFQ.Create') && (
              <button onClick={openNewRFQ}
                className="flex items-center gap-2 px-4 py-2 bg-[#1C1A18] text-white rounded-xl text-sm font-medium hover:bg-[#2E2C29]">
                + New RFQ
              </button>
            )}
            {selectedProject && mainTab === 'equipment' && eqActiveTab === 'rfq' && can('RFQ.Create') && (
              <button onClick={openNewEqRFQ}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600">
                + New Equipment RFQ
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{padding:"20px 24px"}}>
        {/* Project Selector */}
        {!selectedProject ? (
          <div className="py-12">
            <p className="text-xs text-[#6B7280] mb-4">اختر مشروع</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {projects.map(p => (
                <button key={p.id} onClick={() => setSelectedProject(p)}
                  className="text-left p-4 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#1A7A4A] transition-colors">
                  <div className="font-semibold text-[#1C1A18] text-sm">{p.name}</div>
                </button>
              ))}
            </div>
          </div>
        ) : mainTab === 'equipment' ? (
          <>
            {eqActiveTab === 'rfq' ? (
              <>
                {/* Equipment KPIs */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total RFQs', value: eqRfqs.length },
                    { label: 'Issued', value: eqRfqs.filter(r => r.status === 'issued').length, color: 'text-blue-600' },
                    { label: 'Closed', value: eqRfqs.filter(r => r.status === 'closed').length, color: 'text-amber-600' },
                    { label: 'Evaluated', value: eqRfqs.filter(r => r.status === 'evaluated').length, color: 'text-[#1A7A4A]' },
                  ].map(k => (
                    <div key={k.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                      <div className="text-xs text-[#9CA3AF] uppercase tracking-wider">{k.label}</div>
                      <div className={`text-2xl font-bold mt-1 ${k.color || 'text-[#1C1A18]'}`}>{k.value}</div>
                    </div>
                  ))}
                </div>

                {/* Equipment RFQ Table */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">RFQ Number</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Title</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Linked PR</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Closing Date</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Quotations</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Status</th>
                        <th className="w-32 px-4 py-3"/>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {eqRfqs.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF] text-sm">لا يوجد RFQs</td></tr>
                      ) : eqRfqs.map(rfq => (
                        <tr key={rfq.id} className="hover:bg-[#FAFAFA] group">
                          <td className="px-4 py-3 font-mono text-xs text-orange-600 font-bold">{rfq.rfq_number}</td>
                          <td className="px-4 py-3 font-medium text-[#1C1A18]">{rfq.title}</td>
                          <td className="px-4 py-3 text-xs text-[#9CA3AF]">
                            {eqPrs.find(p => p.id === rfq.pr_id) ? `${eqPrs.find(p => p.id === rfq.pr_id).pr_number} — ${eqPrs.find(p => p.id === rfq.pr_id).description||''}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-[#6B7280] text-xs">{rfq.closing_date || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EDE7DA] text-xs font-bold text-[#1C1A18]">
                              {eqQuotations.filter(q=>q.rfq_id===rfq.id).length || 0}
                            </span>
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={rfq.status} config={RFQ_STATUS}/></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openEqComparison(rfq)}
                                className="px-2 py-1 text-xs text-[#6B7280] border border-[#E5E7EB] rounded hover:bg-[#EDE7DA]">📊</button>
                              {can('RFQ.Edit') && (
                                <button onClick={() => openAddEqQuote(rfq)}
                                  className="px-2 py-1 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50">+ Quote</button>
                              )}
                              {rfq.status === 'draft' && (
                                <button onClick={() => handleStatusEqRFQ(rfq, 'issued')}
                                  className="px-2 py-1 text-xs text-[#1A7A4A] border border-green-200 rounded hover:bg-green-50">Issue</button>
                              )}
                              {rfq.status === 'issued' && (
                                <button onClick={() => handleStatusEqRFQ(rfq, 'closed')}
                                  className="px-2 py-1 text-xs text-amber-600 border border-amber-200 rounded hover:bg-amber-50">Close</button>
                              )}
                              {rfq.status !== 'evaluated' && can('RFQ.Delete') && (
                                <button onClick={() => setEqConfirmDelete(rfq)}
                                  className="px-2 py-1 text-xs text-red-600 border border-red-100 rounded hover:bg-red-50">🗑</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              /* Equipment Comparison Tab */
              <div>
                <div className="mb-6">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase mb-2 block">Select RFQ</label>
                  <div className="flex gap-2 flex-wrap">
                    {eqRfqs.map(rfq => (
                      <button key={rfq.id} onClick={() => { setEqSelectedRFQ(rfq); loadEqQuotations(rfq.id) }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${eqSelectedRFQ?.id === rfq.id ? 'bg-[#1C1A18] text-white border-[#1C1A18]' : 'bg-white border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                        {rfq.rfq_number} — {rfq.title}
                      </button>
                    ))}
                  </div>
                </div>

                {eqSelectedRFQ && eqQuotations.length > 0 && (
                  <>
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                        <div className="text-xs text-[#9CA3AF] uppercase">Quotations Received</div>
                        <div className="text-xl font-extrabold text-[#0F1017] tracking-tight mt-1">{eqQuotations.length}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-green-200 p-4">
                        <div className="text-xs text-[#9CA3AF] uppercase">Lowest</div>
                        <div className="text-2xl font-bold text-[#1A7A4A] mt-1">SAR {fmtN(eqLowest)}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                        <div className="text-xs text-[#9CA3AF] uppercase">Average</div>
                        <div className="text-2xl font-bold text-[#6B7280] mt-1">SAR {fmtN(eqAverage)}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-blue-200 p-4">
                        <div className="text-xs text-[#9CA3AF] uppercase">Recommended</div>
                        <div className="text-lg font-bold text-blue-700 mt-1">{eqRecommended?.supplier_name || '—'}</div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto mb-6">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase w-48">Criteria</th>
                            {eqQuotations.map(q => (
                              <th key={q.id} className={`text-center px-4 py-3 text-xs font-semibold uppercase min-w-[180px]
                                ${q.is_recommended ? 'text-[#1A7A4A] bg-green-50' : 'text-[#6B7280]'}`}>
                                {q.supplier_name}
                                {q.is_recommended && <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">✓ Recommended</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                          <tr className="bg-[#F5F1EA]">
                            <td className="px-4 py-3 font-bold text-[#1C1A18] text-sm">Total Amount</td>
                            {eqQuotations.map(q => (
                              <td key={q.id} className={`px-4 py-3 text-center font-bold text-base
                                ${q.total_amount === eqLowest ? 'text-[#1A7A4A]' : 'text-[#1C1A18]'}`}>
                                SAR {fmtN(q.total_amount)}
                                {q.total_amount === eqLowest && <span className="ml-1 text-xs text-[#1A7A4A]">★ Lowest</span>}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">vs Average</td>
                            {eqQuotations.map(q => {
                              const diff = q.total_amount - eqAverage
                              return (
                                <td key={q.id} className={`px-4 py-3 text-center text-xs font-medium ${diff > 0 ? 'text-red-500' : 'text-[#1A7A4A]'}`}>
                                  {diff > 0 ? '+' : ''}{fmtN(diff)} ({diff > 0 ? '+' : ''}{eqAverage ? ((diff/eqAverage)*100).toFixed(1) : 0}%)
                                </td>
                              )
                            })}
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">Delivery Days</td>
                            {eqQuotations.map(q => <td key={q.id} className="px-4 py-3 text-center text-xs">{q.delivery_days || '—'}</td>)}
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">Payment Terms</td>
                            {eqQuotations.map(q => <td key={q.id} className="px-4 py-3 text-center text-xs">{q.payment_terms || '—'}</td>)}
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">Validity</td>
                            {eqQuotations.map(q => <td key={q.id} className="px-4 py-3 text-center text-xs">{q.validity_days} days</td>)}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {eqQuotations.map(q => (
                        <div key={q.id} className="flex items-center gap-2">
                          {!q.is_recommended ? (
                            can('RFQ.Edit') && (
                              <button onClick={() => handleRecommendEq(eqSelectedRFQ.id, q.id)}
                                className="px-3 py-1.5 text-xs border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50">
                                ✓ Recommend {q.supplier_name}
                              </button>
                            )
                          ) : (
                            <>
                              {can('RFQ.Edit') && (
                                <button onClick={() => handleUnrecommendEq(eqSelectedRFQ.id)}
                                  className="px-3 py-1.5 text-xs border border-gray-300 text-[#6B7280] rounded-lg hover:bg-[#FAFAFA]">
                                  Unrecommend
                                </button>
                              )}
                              {can('RFQ.Award') && (
                                <button onClick={() => handleAwardAndCreateEqPO(eqSelectedRFQ, q)}
                                  className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium">
                                  🏆 Award & Create Equipment PO
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        ) : activeTab === 'rfq' ? (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total RFQs', value: rfqs.length },
                { label: 'Issued', value: rfqs.filter(r => r.status === 'issued').length, color: 'text-blue-600' },
                { label: 'Closed', value: rfqs.filter(r => r.status === 'closed').length, color: 'text-amber-600' },
                { label: 'Evaluated', value: rfqs.filter(r => r.status === 'evaluated').length, color: 'text-[#1A7A4A]' },
              ].map(k => (
                <div key={k.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                  <div className="text-xs text-[#9CA3AF] uppercase tracking-wider">{k.label}</div>
                  <div className={`text-2xl font-bold mt-1 ${k.color || 'text-[#1C1A18]'}`}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* RFQ Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-[#1A7A4A]/40 border-t-[#1A7A4A] rounded-full animate-spin"/>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">RFQ Number</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Title</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Linked PR</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Closing Date</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Quotations</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Status</th>
                      <th className="w-32 px-4 py-3"/>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {rfqs.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF] text-sm">لا يوجد RFQs</td></tr>
                    ) : rfqs.map(rfq => (
                      <tr key={rfq.id} className="hover:bg-[#FAFAFA] group">
                        <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A] font-bold">{rfq.rfq_number}</td>
                        <td className="px-4 py-3 font-medium text-[#1C1A18]">{rfq.title}</td>
                        <td className="px-4 py-3 text-xs text-[#9CA3AF]">
                          {rfq.purchase_requisitions ? `${rfq.purchase_requisitions.pr_number} — ${rfq.purchase_requisitions.description||''}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-[#6B7280] text-xs">{rfq.closing_date || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EDE7DA] text-xs font-bold text-[#1C1A18]">
                            {rfq.quotations?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={rfq.status} config={RFQ_STATUS}/></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openComparison(rfq)}
                              className="px-2 py-1 text-xs text-[#6B7280] border border-[#E5E7EB] rounded hover:bg-[#EDE7DA]">📊</button>
                            {can('RFQ.Edit') && (
                              <button onClick={() => openAddQuote(rfq)}
                                className="px-2 py-1 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50">+ Quote</button>
                            )}
                            {rfq.status === 'draft' && (
                              <button onClick={() => handleStatusRFQ(rfq, 'issued')}
                                className="px-2 py-1 text-xs text-[#1A7A4A] border border-green-200 rounded hover:bg-green-50">Issue</button>
                            )}
                            {rfq.status === 'issued' && (
                              <button onClick={() => handleStatusRFQ(rfq, 'closed')}
                                className="px-2 py-1 text-xs text-amber-600 border border-amber-200 rounded hover:bg-amber-50">Close</button>
                            )}
                            {rfq.status !== 'evaluated' && can('RFQ.Delete') && (
                              <button onClick={() => setConfirmDelete(rfq)}
                                className="px-2 py-1 text-xs text-red-600 border border-red-100 rounded hover:bg-red-50">🗑</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          /* Comparison Tab */
          <div>
            {/* RFQ Selector */}
            <div className="mb-6">
              <label className="text-xs font-semibold text-[#6B7280] uppercase mb-2 block">Select RFQ</label>
              <div className="flex gap-2 flex-wrap">
                {rfqs.map(rfq => (
                  <button key={rfq.id} onClick={() => { setSelectedRFQ(rfq); loadQuotations(rfq.id) }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${selectedRFQ?.id === rfq.id ? 'bg-[#1C1A18] text-white border-[#1C1A18]' : 'bg-white border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                    {rfq.rfq_number} — {rfq.title}
                  </button>
                ))}
              </div>
            </div>

            {selectedRFQ && quotations.length > 0 && (
              <>
                {/* Summary KPIs */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                    <div className="text-xs text-[#9CA3AF] uppercase">Quotations Received</div>
                    <div className="text-xl font-extrabold text-[#0F1017] tracking-tight mt-1">{quotations.length}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-green-200 p-4">
                    <div className="text-xs text-[#9CA3AF] uppercase">Lowest</div>
                    <div className="text-2xl font-bold text-[#1A7A4A] mt-1">SAR {fmtN(lowest)}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                    <div className="text-xs text-[#9CA3AF] uppercase">Average</div>
                    <div className="text-2xl font-bold text-[#6B7280] mt-1">SAR {fmtN(average)}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-blue-200 p-4">
                    <div className="text-xs text-[#9CA3AF] uppercase">Recommended</div>
                    <div className="text-lg font-bold text-blue-700 mt-1">{recommended?.supplier_name || '—'}</div>
                  </div>
                </div>

                {/* Comparison Table */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase w-48">Criteria</th>
                        {quotations.map(q => (
                          <th key={q.id} className={`text-center px-4 py-3 text-xs font-semibold uppercase min-w-[180px]
                            ${q.is_recommended ? 'text-[#1A7A4A] bg-green-50' : 'text-[#6B7280]'}`}>
                            {q.supplier_name}
                            {q.is_recommended && <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">✓ Recommended</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {/* Total Amount */}
                      <tr className="bg-[#F5F1EA]">
                        <td className="px-4 py-3 font-bold text-[#1C1A18] text-sm">Total Amount</td>
                        {quotations.map(q => (
                          <td key={q.id} className={`px-4 py-3 text-center font-bold text-base
                            ${q.total_amount === lowest ? 'text-[#1A7A4A]' : 'text-[#1C1A18]'}`}>
                            SAR {fmtN(q.total_amount)}
                            {q.total_amount === lowest && <span className="ml-1 text-xs text-[#1A7A4A]">★ Lowest</span>}
                          </td>
                        ))}
                      </tr>
                      {/* vs Average */}
                      <tr>
                        <td className="px-4 py-3 text-[#6B7280] text-xs">vs Average</td>
                        {quotations.map(q => {
                          const diff = q.total_amount - average
                          return (
                            <td key={q.id} className={`px-4 py-3 text-center text-xs font-medium ${diff > 0 ? 'text-red-500' : 'text-[#1A7A4A]'}`}>
                              {diff > 0 ? '+' : ''}{fmtN(diff)} ({diff > 0 ? '+' : ''}{average ? ((diff/average)*100).toFixed(1) : 0}%)
                            </td>
                          )
                        })}
                      </tr>
                      {/* Delivery */}
                      <tr>
                        <td className="px-4 py-3 text-[#6B7280]">Delivery (days)</td>
                        {quotations.map(q => (
                          <td key={q.id} className="px-4 py-3 text-center text-[#6B7280]">{q.delivery_days || '—'}</td>
                        ))}
                      </tr>
                      {/* Validity */}
                      <tr>
                        <td className="px-4 py-3 text-[#6B7280]">Validity (days)</td>
                        {quotations.map(q => (
                          <td key={q.id} className="px-4 py-3 text-center text-[#6B7280]">{q.validity_days || '—'}</td>
                        ))}
                      </tr>
                      {/* Payment Terms */}
                      <tr>
                        <td className="px-4 py-3 text-[#6B7280]">Payment Terms</td>
                        {quotations.map(q => (
                          <td key={q.id} className="px-4 py-3 text-center text-[#6B7280] text-xs">{q.payment_terms || '—'}</td>
                        ))}
                      </tr>
                      {/* Line items */}
                      {quotations[0]?.quotation_line_items?.length > 0 && (
                        <>
                          <tr className="bg-[#EDE7DA]">
                            <td colSpan={quotations.length + 1} className="px-4 py-2 text-xs font-bold text-[#6B7280] uppercase">Line Item Breakdown</td>
                          </tr>
                          {quotations[0].quotation_line_items.map((_, lineIdx) => (
                            <tr key={lineIdx}>
                              <td className="px-4 py-2.5 text-[#6B7280] text-xs pl-8">
                                {quotations[0].quotation_line_items[lineIdx]?.description}
                              </td>
                              {quotations.map(q => {
                                const line = q.quotation_line_items?.[lineIdx]
                                return (
                                  <td key={q.id} className="px-4 py-2.5 text-center text-xs text-[#6B7280]">
                                    {line ? `${line.quantity || ''} ${line.unit || ''} × ${fmtN(line.unit_rate)} = ${fmtN((line.quantity || 0) * (line.unit_rate || 0))}` : '—'}
                                  </td>
                                )
                              })}
                            </tr>
                          ))}
                        </>
                      )}
                      {/* Recommend + Award row */}
                      <tr className="bg-[#F5F1EA]">
                        <td className="px-4 py-3 font-semibold text-[#1C1A18]">Recommendation</td>
                        {quotations.map(q => {
                          // Check if any quotation has a PO (awarded + locked)
                          const hasAwardedPO = quotations.some(x => x.status === 'awarded' && x.po_id)
                          const isLocked = q.status === 'awarded' && q.po_id

                          return (
                            <td key={q.id} className="px-4 py-3 text-center">
                              {isLocked ? (
                                // Awarded + has PO → fully locked
                                <div className="flex flex-col items-center gap-1">
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">🏆 Awarded</span>
                                  <span className="text-[10px] text-[#9CA3AF]">PO created</span>
                                </div>
                              ) : hasAwardedPO ? (
                                // Another quotation is locked → disable all others
                                <span className="text-xs text-[#9CA3AF]">—</span>
                              ) : q.status === 'awarded' ? (
                                // Awarded but no PO yet → show Award button + un-award option
                                <div className="flex flex-col items-center gap-1.5">
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">✓ Awarded</span>
                                  {can('RFQ.Award') && (
                                    <button onClick={() => handleAwardAndCreatePO(selectedRFQ, q)}
                                      className="px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-semibold hover:bg-[#1B3A6B]/90">
                                      🏆 Create PO
                                    </button>
                                  )}
                                  {can('RFQ.Edit') && (
                                    <button onClick={() => handleUnrecommend(selectedRFQ.id)}
                                      className="text-[10px] text-red-400 hover:text-red-600">✕ Remove</button>
                                  )}
                                </div>
                              ) : q.is_recommended ? (
                                // Recommended → show Award button + remove option
                                <div className="flex flex-col items-center gap-1.5">
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">✓ Recommended</span>
                                  {can('RFQ.Award') && (
                                    <button onClick={() => handleAwardAndCreatePO(selectedRFQ, q)}
                                      className="px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-semibold hover:bg-[#1B3A6B]/90">
                                      🏆 Award & Create PO
                                    </button>
                                  )}
                                  {can('RFQ.Edit') && (
                                    <button onClick={() => handleUnrecommend(selectedRFQ.id)}
                                      className="text-[10px] text-red-400 hover:text-red-600">✕ Remove</button>
                                  )}
                                </div>
                              ) : can('RFQ.Edit') ? (
                                // Default → Recommend button (disabled if another is recommended)
                                <button
                                  onClick={() => handleRecommend(selectedRFQ.id, q.id)}
                                  disabled={quotations.some(x => x.is_recommended || x.status === 'awarded')}
                                  className="px-3 py-1 border border-[#E5E7EB] text-xs text-[#6B7280] rounded-lg hover:bg-[#EDE7DA] disabled:opacity-40 disabled:cursor-not-allowed">
                                  Recommend
                                </button>
                              ) : null}
                            </td>
                          )
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Add quote button */}
                {can('RFQ.Edit') && (
                  <button onClick={() => openAddQuote(selectedRFQ)}
                    className="px-4 py-2 bg-[#1C1A18] text-white rounded-xl text-sm font-medium hover:bg-[#2E2C29]">
                    + Add Quotation
                  </button>
                )}
              </>
            )}

            {selectedRFQ && quotations.length === 0 && (
              <div className="text-center py-16 text-[#9CA3AF]">
                <div className="text-4xl mb-3">📋</div>
                <p className="text-sm">لا يوجد quotations لهذا الـ RFQ</p>
                {can('RFQ.Edit') && (
                  <button onClick={() => openAddQuote(selectedRFQ)}
                    className="mt-4 px-4 py-2 bg-[#1C1A18] text-white rounded-xl text-sm font-medium hover:bg-[#2E2C29]">
                    + Add First Quotation
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* RFQ Modal */}
      {showRFQModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#F5F1EA] rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold text-[#1C1A18]">New RFQ</h2>
              <button onClick={() => setShowRFQModal(false)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Title *</label>
                <input value={rfqForm.title} onChange={e => setRfqForm(p => ({...p, title: e.target.value}))}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Scope Description</label>
                <textarea value={rfqForm.scope_description} onChange={e => setRfqForm(p => ({...p, scope_description: e.target.value}))} rows={3}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white resize-none"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Linked PR</label>
                  <select value={rfqForm.pr_id} onChange={e => setRfqForm(p => ({...p, pr_id: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white">
                    <option value="">— None —</option>
                    {prs.map(pr => <option key={pr.id} value={pr.id}>{pr.pr_number} — {pr.description}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Closing Date</label>
                  <input type="date" value={rfqForm.closing_date} onChange={e => setRfqForm(p => ({...p, closing_date: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowRFQModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={handleSaveRFQ} className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg text-sm font-medium hover:bg-[#2E2C29]">Create RFQ</button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment New RFQ Modal */}
      {eqShowRFQModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#F5F1EA] rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold text-[#1C1A18]">New Equipment RFQ</h2>
              <button onClick={() => setEqShowRFQModal(false)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Title *</label>
                <input value={eqRfqForm.title} onChange={e => setEqRfqForm(p => ({...p, title: e.target.value}))}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Scope Description</label>
                <textarea value={eqRfqForm.scope_description} onChange={e => setEqRfqForm(p => ({...p, scope_description: e.target.value}))} rows={3}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white resize-none"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Linked Equipment PR</label>
                  <select value={eqRfqForm.pr_id} onChange={e => setEqRfqForm(p => ({...p, pr_id: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white">
                    <option value="">— None —</option>
                    {eqPrs.map(pr => <option key={pr.id} value={pr.id}>{pr.pr_number} — {pr.description}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Closing Date</label>
                  <input type="date" value={eqRfqForm.closing_date} onChange={e => setEqRfqForm(p => ({...p, closing_date: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setEqShowRFQModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={handleSaveEqRFQ} className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">Create Equipment RFQ</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Quotation Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#F5F1EA] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold text-[#1C1A18]">Add Quotation — {selectedRFQ?.rfq_number}</h2>
              <div className="flex items-center gap-2">
                <button onClick={downloadQuoteTemplate} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">↓ Template</button>
                <label className="px-3 py-1.5 text-xs border border-[#1B3A6B] text-[#1B3A6B] rounded-lg hover:bg-[#1B3A6B]/5 cursor-pointer">
                  ↑ Upload Excel
                  <input ref={quoteFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleQuoteExcelUpload(e.target.files[0])}/>
                </label>
                <button onClick={() => setShowQuoteModal(false)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl ml-2">✕</button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Vendor (from Vendor Master) *</label>
                  <input
                    value={vendorSearch}
                    onChange={e => { setVendorSearch(e.target.value); setQuoteForm(p => ({...p, supplier_name: e.target.value, vendor_id: ''})); searchVendors(e.target.value); setShowVendorDD(true) }}
                    onFocus={() => setShowVendorDD(true)}
                    onBlur={() => setTimeout(() => setShowVendorDD(false), 150)}
                    placeholder="Type vendor name…"
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white"/>
                  {quoteForm.vendor_id && <span className="absolute right-3 top-8 text-[#1A7A4A] text-xs">✓ linked</span>}
                  {showVendorDD && vendorResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {vendorResults.map(v => (
                        <button key={v.id} type="button" onMouseDown={() => selectVendor(v)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[#F3F4F6] border-b border-[#F3F4F6] last:border-0">
                          <div className="font-medium text-[#1C1A18]">{v.trade_name}</div>
                          <div className="text-xs text-[#9CA3AF]">{v.legal_name} {v.vat_number ? `· VAT ${v.vat_number}` : ''}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {vendorSearch.length >= 2 && vendorResults.length === 0 && !quoteForm.vendor_id && (
                    <p className="text-xs text-amber-600 mt-1">No match in Vendor Master — add this vendor there first.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Quotation Ref</label>
                  <input value={quoteForm.quotation_ref} onChange={e => setQuoteForm(p => ({...p, quotation_ref: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Submission Date</label>
                  <input type="date" value={quoteForm.submission_date} onChange={e => setQuoteForm(p => ({...p, submission_date: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Validity (days)</label>
                  <input type="number" value={quoteForm.validity_days} onChange={e => setQuoteForm(p => ({...p, validity_days: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Delivery (days)</label>
                  <input type="number" value={quoteForm.delivery_days} onChange={e => setQuoteForm(p => ({...p, delivery_days: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Payment Terms</label>
                <input value={quoteForm.payment_terms} onChange={e => setQuoteForm(p => ({...p, payment_terms: e.target.value}))}
                  placeholder="e.g. 30 days net, 50% advance"
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
              </div>
              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Line Items</label>
                  <div className="flex items-center gap-2">
                    <button onClick={downloadQuoteTemplate} className="text-xs text-[#6B7280] hover:underline">↓ Template</button>
                    <label className="text-xs text-[#1B3A6B] hover:underline cursor-pointer">
                      ↑ Upload Excel
                      <input ref={quoteFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleQuoteExcelUpload(e.target.files[0])}/>
                    </label>
                    <button onClick={() => setQuoteLines(l => [...l, { ...EMPTY_QUOTE_LINE }])} className="text-xs text-[#1A7A4A] hover:underline">+ Add Line</button>
                  </div>
                </div>
                <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#EDE7DA]">
                      <tr>
                        <th className="text-left px-3 py-2">Description</th>
                        <th className="text-left px-2 py-2 w-40">Sub Code</th>
                        <th className="text-left px-2 py-2 w-32">Cost Code</th>
                        <th className="text-left px-2 py-2 w-28 text-purple-700">Resource Code</th>
                        <th className="text-left px-2 py-2 w-36">Ref Code (Material)</th>
                        <th className="text-center px-2 py-2 w-16">Unit</th>
                        <th className="text-right px-2 py-2 w-16">Qty</th>
                        <th className="text-right px-2 py-2 w-20">Rate</th>
                        <th className="text-right px-2 py-2 w-20">Total</th>
                        <th className="w-6 px-2"/>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6] bg-white">
                      {quoteLines.map((line, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1.5"><input value={line.description || ''} onChange={e => setQuoteLines(l => l.map((x,j) => j===i?{...x,description:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent"/></td>
                          <td className="px-2 py-1.5">
                            <select value={line.sub_code || ''} onChange={e => {
                              const sc = subCodes.find(s => s.code === e.target.value)
                              setQuoteLines(l => l.map((x,j) => j===i?{...x, sub_code: e.target.value, cost_code: sc?.costCode || '', cost_code_desc: sc?.costCodeDescription || ''}:x))
                            }}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent font-mono text-[#1B3A6B]">
                              <option value="">—</option>
                              {subCodes.map(s => <option key={s.id} value={s.code}>{s.code} — {s.description}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 text-xs text-[#6B7280]">
                            {line.cost_code ? <span className="font-mono text-[#1B3A6B]">{line.cost_code}</span> : '—'}
                            {line.cost_code_desc && <span className="text-[#9CA3AF] ml-1 text-[10px]">{line.cost_code_desc}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            {(() => { const sc = subCodes.find(s => s.code === line.sub_code); return sc?.resourceCode ? (
                              <span className="font-mono text-purple-700">{sc.resourceCode}</span>
                            ) : '—' })()}
                          </td>
                          <td className="px-2 py-1.5">
                            <select value={line.ref_code || ''} onChange={e => {
                              const mat = materials.find(m => m.material_code === e.target.value)
                              // Always prefer the newly-selected material's
                              // description when one exists, so switching
                              // ref_code updates the description immediately
                              // instead of only on the first (empty) pick.
                              // Falls back to the existing manual text only
                              // when the chosen ref_code has no matching
                              // material (or is cleared).
                              setQuoteLines(l => l.map((x,j) => j===i?{...x, ref_code: e.target.value, unit: mat?.unit || x.unit, description: mat?.description || x.description || ''}:x))
                            }}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent font-mono text-[#1A7A4A]">
                              <option value="">—</option>
                              {materials.map(m => <option key={m.id} value={m.material_code}>{m.material_code}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input list={`unit-${i}`} value={line.unit || ''} onChange={e => setQuoteLines(l => l.map((x,j) => j===i?{...x,unit:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent text-center"/>
                            <datalist id={`unit-${i}`}>{['M','M2','M3','ML','KG','MT','TON','PC','SET','LOT','EACH','MONTH','DAY'].map(u=><option key={u} value={u}/>)}</datalist>
                          </td>
                          <td className="px-2 py-1"><input type="number" value={line.quantity || ''} onChange={e => setQuoteLines(l => l.map((x,j) => j===i?{...x,quantity:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent text-right"/></td>
                          <td className="px-2 py-1"><input type="number" value={line.unit_rate || ''} onChange={e => setQuoteLines(l => l.map((x,j) => j===i?{...x,unit_rate:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent text-right"/></td>
                          <td className="px-2 py-1 text-right font-semibold text-[#1A7A4A]">{line.quantity && line.unit_rate ? fmtN(line.quantity * line.unit_rate) : '—'}</td>
                          <td className="px-2 py-1">{quoteLines.length > 1 && <button onClick={() => setQuoteLines(l => l.filter((_,j) => j!==i))} className="text-[#A32D2D]">×</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Notes</label>
                <textarea value={quoteForm.notes} onChange={e => setQuoteForm(p => ({...p, notes: e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white resize-none"/>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowQuoteModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={handleSaveQuote} className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg text-sm font-medium hover:bg-[#2E2C29]">Add Quotation</button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment Add Quotation Modal */}
      {eqShowQuoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#F5F1EA] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold text-[#1C1A18]">Add Equipment Quotation — {eqSelectedRFQ?.rfq_number}</h2>
              <div className="flex items-center gap-2">
                <button onClick={downloadEqQuoteTemplate} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">↓ Template</button>
                <label className="px-3 py-1.5 text-xs border border-orange-400 text-orange-600 rounded-lg hover:bg-orange-50 cursor-pointer">
                  ↑ Upload Excel
                  <input ref={eqQuoteFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleEqQuoteExcelUpload(e.target.files[0])}/>
                </label>
                <button onClick={() => setEqShowQuoteModal(false)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl ml-2">✕</button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Vendor (from Vendor Master) *</label>
                  <input
                    value={eqVendorSearch}
                    onChange={e => { setEqVendorSearch(e.target.value); setEqQuoteForm(p => ({...p, supplier_name: e.target.value, vendor_id: ''})); searchEqVendors(e.target.value); setEqShowVendorDD(true) }}
                    onFocus={() => setEqShowVendorDD(true)}
                    onBlur={() => setTimeout(() => setEqShowVendorDD(false), 150)}
                    placeholder="Type vendor name…"
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"/>
                  {eqQuoteForm.vendor_id && <span className="absolute right-3 top-8 text-[#1A7A4A] text-xs">✓ linked</span>}
                  {eqShowVendorDD && eqVendorResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {eqVendorResults.map(v => (
                        <button key={v.id} type="button" onMouseDown={() => selectEqVendor(v)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[#F3F4F6] border-b border-[#F3F4F6] last:border-0">
                          <div className="font-medium text-[#1C1A18]">{v.trade_name}</div>
                          <div className="text-xs text-[#9CA3AF]">{v.legal_name} {v.vat_number ? `· VAT ${v.vat_number}` : ''}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {eqVendorSearch.length >= 2 && eqVendorResults.length === 0 && !eqQuoteForm.vendor_id && (
                    <p className="text-xs text-amber-600 mt-1">No match in Vendor Master — add this vendor there first.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Quotation Ref</label>
                  <input value={eqQuoteForm.quotation_ref} onChange={e => setEqQuoteForm(p => ({...p, quotation_ref: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Submission Date</label>
                  <input type="date" value={eqQuoteForm.submission_date} onChange={e => setEqQuoteForm(p => ({...p, submission_date: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Validity (days)</label>
                  <input type="number" value={eqQuoteForm.validity_days} onChange={e => setEqQuoteForm(p => ({...p, validity_days: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Delivery (days)</label>
                  <input type="number" value={eqQuoteForm.delivery_days} onChange={e => setEqQuoteForm(p => ({...p, delivery_days: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Payment Terms</label>
                <input value={eqQuoteForm.payment_terms} onChange={e => setEqQuoteForm(p => ({...p, payment_terms: e.target.value}))}
                  placeholder="e.g. 30 days net, 50% advance"
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
              </div>
              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Line Items</label>
                  <div className="flex items-center gap-2">
                    <button onClick={downloadEqQuoteTemplate} className="text-xs text-[#6B7280] hover:underline">↓ Template</button>
                    <label className="text-xs text-orange-600 hover:underline cursor-pointer">
                      ↑ Upload Excel
                      <input ref={eqQuoteFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleEqQuoteExcelUpload(e.target.files[0])}/>
                    </label>
                    <button onClick={() => setEqQuoteLines(l => [...l, { ...EMPTY_EQ_QUOTE_LINE }])} className="text-xs text-[#1A7A4A] hover:underline">+ Add Line</button>
                  </div>
                </div>
                <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#EDE7DA]">
                      <tr>
                        <th className="text-left px-3 py-2">Equipment Type</th>
                        <th className="text-left px-2 py-2">Description</th>
                        <th className="text-left px-2 py-2 w-32">Cost Code</th>
                        <th className="text-center px-2 py-2 w-16">Unit</th>
                        <th className="text-right px-2 py-2 w-16">Qty</th>
                        <th className="text-right px-2 py-2 w-20">Rate</th>
                        <th className="text-right px-2 py-2 w-20">Total</th>
                        <th className="w-6 px-2"/>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6] bg-white">
                      {eqQuoteLines.map((line, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1.5">
                            <input list="eq-quote-type-options" value={line.equipment_type || ''} onChange={e => setEqQuoteLines(l => l.map((x,j) => j===i?{...x,equipment_type:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent" placeholder="Excavator…"/>
                          </td>
                          <td className="px-2 py-1.5"><input value={line.description || ''} onChange={e => setEqQuoteLines(l => l.map((x,j) => j===i?{...x,description:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent"/></td>
                          <td className="px-2 py-1.5">
                            <select value={line.cost_code || ''} onChange={e => {
                              const cc = eqCostCodes.find(c => c.code === e.target.value)
                              setEqQuoteLines(l => l.map((x,j) => j===i?{...x, cost_code: e.target.value, cost_code_desc: cc?.description || ''}:x))
                            }}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent font-mono text-[#1B3A6B]">
                              <option value="">—</option>
                              {eqCostCodes.map(c => <option key={c.id} value={c.code}>{c.code} — {c.description}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input list={`eq-unit-${i}`} value={line.unit || ''} onChange={e => setEqQuoteLines(l => l.map((x,j) => j===i?{...x,unit:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent text-center"/>
                            <datalist id={`eq-unit-${i}`}>{['Hours','Days','Months','LS'].map(u=><option key={u} value={u}/>)}</datalist>
                          </td>
                          <td className="px-2 py-1"><input type="number" value={line.quantity || ''} onChange={e => setEqQuoteLines(l => l.map((x,j) => j===i?{...x,quantity:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent text-right"/></td>
                          <td className="px-2 py-1"><input type="number" value={line.unit_rate || ''} onChange={e => setEqQuoteLines(l => l.map((x,j) => j===i?{...x,unit_rate:e.target.value}:x))} className="w-full border-0 focus:outline-none text-xs bg-transparent text-right"/></td>
                          <td className="px-2 py-1 text-right font-semibold text-[#1A7A4A]">{line.quantity && line.unit_rate ? fmtN(line.quantity * line.unit_rate) : '—'}</td>
                          <td className="px-2 py-1">{eqQuoteLines.length > 1 && <button onClick={() => setEqQuoteLines(l => l.filter((_,j) => j!==i))} className="text-[#A32D2D]">×</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <datalist id="eq-quote-type-options">
                  {[...new Set(eqQuotations.flatMap(q=>q.equipment_quotation_line_items||[]).map(l=>l.equipment_type).filter(Boolean))].map(t=><option key={t} value={t}/>)}
                </datalist>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Notes</label>
                <textarea value={eqQuoteForm.notes} onChange={e => setEqQuoteForm(p => ({...p, notes: e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white resize-none"/>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setEqShowQuoteModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={handleSaveEqQuote} className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">Add Quotation</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmOverlay
          message={`حذف "${confirmDelete.rfq_number}"؟`}
          onConfirm={() => handleDeleteRFQ(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {eqConfirmDelete && (
        <ConfirmOverlay
          message={`حذف "${eqConfirmDelete.rfq_number}"؟`}
          onConfirm={() => handleDeleteEqRFQ(eqConfirmDelete)}
          onCancel={() => setEqConfirmDelete(null)}
        />
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)}/>}
    </div>
  )
}
